import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';
import { TIMEZONE_OPTIONS, DEFAULT_TIMEZONE_ID } from '../../utils/timezones';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function digitsOnly(value) {
  return (value || '').replace(/\D/g, '');
}

function formatOwnerPhoneInput(value) {
  const digits = digitsOnly(value);
  if (!digits) return '';

  // US/Canada formatting (supports optional leading country code 1)
  if (digits.length >= 11 && digits.startsWith('1')) {
    const core = digits.slice(1, 11);
    const parts = [];
    if (core.slice(0, 3)) parts.push(`(${core.slice(0, 3)})`);
    if (core.slice(3, 6)) parts.push(core.slice(3, 6));
    const suffix = core.slice(6, 10);
    return `${parts.join(' ')}${suffix ? `-${suffix}` : ''}`;
  }

  const core = digits.slice(0, 10);
  if (core.length <= 3) return core;
  if (core.length <= 6) return `(${core.slice(0, 3)}) ${core.slice(3)}`;
  return `(${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6, 10)}`;
}

function normalizeOwnerPhoneForSave(value) {
  const digits = digitsOnly(value);
  if (!digits) return null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return value.trim() || null;
}

export default function BusinessSettings() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [timeZoneId, setTimeZoneId] = useState(DEFAULT_TIMEZONE_ID);
  const [ownerNotificationPhone, setOwnerNotificationPhone] = useState('');
  const [operatingHours, setOperatingHours] = useState(
    DAY_NAMES.map((_, i) => ({ dayOfWeek: i, openTime: '', closeTime: '' }))
  );
  const [isManuallyClosed, setIsManuallyClosed] = useState(false);
  const [storeStatusSaving, setStoreStatusSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Load Google Places Autocomplete for pickup address
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || !addressInputRef.current) return;

    const loadScript = () => {
      if (window.google?.maps?.places) {
        initAutocomplete();
        return;
      }
      if (document.querySelector('script[src*="maps.googleapis.com/maps/api"]')) return;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    };

    const initAutocomplete = () => {
      if (!addressInputRef.current || autocompleteRef.current) return;
      const ac = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
        fields: ['formatted_address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place?.formatted_address) {
          setPickupAddress(place.formatted_address);
        }
      });
      autocompleteRef.current = ac;
    };

    loadScript();
  }, [loading]);

  const fetchBusiness = useCallback(async () => {
    const res = await api('/api/owners/me/business', { getToken });
    if (!res.ok) {
      if (res.status === 403) {
        navigate('/', { replace: true });
        return null;
      }
      setError('Failed to load business');
      return null;
    }
    const data = await res.json();
    setBusiness(data);
    setName(data.name || '');
    setLogoUrl(data.logoUrl || '');
    setPickupAddress(data.pickupAddress || '');
    setTimeZoneId(data.timeZoneId || DEFAULT_TIMEZONE_ID);
    setOwnerNotificationPhone(formatOwnerPhoneInput(data.ownerNotificationPhone || ''));
    setIsManuallyClosed(!!data.isManuallyClosed);
    if (data.operatingHours?.length === 7) {
      setOperatingHours(data.operatingHours);
    } else if (data.operatingHours?.length) {
      const hours = DAY_NAMES.map((_, i) => {
        const h = data.operatingHours.find((x) => x.dayOfWeek === i);
        return h || { dayOfWeek: i, openTime: '', closeTime: '' };
      });
      setOperatingHours(hours);
    }
    setError(null);
    return data;
  }, [getToken, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchBusiness();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchBusiness]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api('/api/owners/me/upload-image', {
        getToken,
        method: 'POST',
        body: formData,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      if (d.url) setLogoUrl(d.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingLogo(false);
    }
    e.target.value = '';
  };

  const setHour = (dayIndex, field, value) => {
    setOperatingHours((prev) =>
      prev.map((h) => (h.dayOfWeek === dayIndex ? { ...h, [field]: value } : h))
    );
  };

  const toggleStoreStatus = async () => {
    if (storeStatusSaving) return;
    const next = !isManuallyClosed;
    // Optimistic UI: flip immediately, revert on failure.
    setIsManuallyClosed(next);
    setStoreStatusSaving(true);
    setError(null);
    try {
      const res = await api('/api/owners/me/business/store-status', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isManuallyClosed: next }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      setIsManuallyClosed(!!d.isManuallyClosed);
    } catch (err) {
      setIsManuallyClosed(!next);
      setError(err.message || 'Could not update store status.');
    } finally {
      setStoreStatusSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const ownerDigits = digitsOnly(ownerNotificationPhone);
    if (ownerDigits && !(ownerDigits.length === 10 || (ownerDigits.length === 11 && ownerDigits.startsWith('1')))) {
      setError('New-order text alerts number must be a valid US/Canada number (10 digits, or 11 starting with 1).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        logoUrl: logoUrl.trim() || null,
        pickupAddress: pickupAddress.trim() || null,
        timeZoneId: timeZoneId || DEFAULT_TIMEZONE_ID,
        ownerNotificationPhone: normalizeOwnerPhoneForSave(ownerNotificationPhone),
        operatingHours: operatingHours.map((h) => ({
          dayOfWeek: h.dayOfWeek,
          openTime: h.openTime?.trim() || null,
          closeTime: h.closeTime?.trim() || null,
        })),
      };
      const res = await api('/api/owners/me/business', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      await fetchBusiness();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !business) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  if (error && !business) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-700 dark:text-red-300">{error}</p>
        <Link
          to="/owner/dashboard"
          className="mt-4 inline-block text-amber-600 hover:text-amber-700"
        >
          ← Back
        </Link>
      </div>
    );
  }

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const logoSrc = logoUrl?.startsWith('/')
    ? baseUrl
      ? `${baseUrl.replace(/\/$/, '')}${logoUrl}`
      : logoUrl
    : logoUrl;

  return (
    <div className="space-y-6 pb-44">
      <div>
        <Link
          to="/owner/dashboard"
          className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-1 inline-block"
        >
          ← Back to dashboard
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Business settings
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Name, time zone, address, hours, and menu link.
        </p>
      </div>

      <form id="business-settings-form" onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Business name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Tacos on Wheels"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 min-h-[48px]"
          />
          <p className="text-xs text-slate-500 mt-1">
            Your menu URL is generated from the name (no spaces). Example: &quot;Tacos on Wheels&quot;
            → /m/tacsonwheels
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Time zone *
          </label>
          <select
            value={timeZoneId}
            onChange={(e) => setTimeZoneId(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 min-h-[48px] text-slate-900 dark:text-slate-100"
            aria-label="Select your business time zone"
          >
            {TIMEZONE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            Used for open/closed hours and sales. Times show in this timezone (e.g. Central 6pm).
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Pickup address
          </label>
          <input
            ref={addressInputRef}
            type="text"
            value={pickupAddress}
            onChange={(e) => setPickupAddress(e.target.value)}
            placeholder="Start typing an address..."
            className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 min-h-[48px]"
          />
          <p className="text-xs text-slate-500 mt-1">
            Shown to customers when their order is ready for pickup. Used for &quot;Get
            directions&quot;.
          </p>
        </div>

        {business?.slug && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Live Menu URL
            </label>
            <a
              href={`${window.location.origin}/m/${business.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-600 hover:text-amber-700 dark:text-amber-500 dark:hover:text-amber-400 underline break-all"
            >
              {window.location.origin}/m/{business.slug}
            </a>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Logo
          </label>
          <div className="flex gap-4 items-start">
            {logoSrc && (
              <img src={logoSrc} alt="Logo" className="w-20 h-20 rounded-full object-cover" />
            )}
            <div>
              <label className="inline-block px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer min-h-[48px]">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                  className="hidden"
                />
                {uploadingLogo ? 'Uploading…' : logoSrc ? 'Change logo' : 'Upload logo'}
              </label>
              {logoSrc && (
                <button
                  type="button"
                  onClick={() => setLogoUrl('')}
                  className="ml-2 text-sm text-red-600 dark:text-red-400"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Store status
          </label>
          <button
            type="button"
            onClick={toggleStoreStatus}
            disabled={storeStatusSaving}
            aria-pressed={!isManuallyClosed}
            className={`w-full rounded-xl px-5 py-5 min-h-[64px] font-semibold text-lg flex items-center justify-between gap-4 transition-colors disabled:opacity-70 ${
              isManuallyClosed
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
          >
            <span className="flex items-center gap-3">
              <span
                className={`inline-flex w-3 h-3 rounded-full ${
                  isManuallyClosed ? 'bg-red-200' : 'bg-emerald-200 animate-pulse'
                }`}
                aria-hidden
              />
              <span>{isManuallyClosed ? 'Store is CLOSED' : 'Store is OPEN'}</span>
            </span>
            <span className="text-sm font-medium opacity-90">
              {storeStatusSaving ? 'Saving…' : `Tap to ${isManuallyClosed ? 'open' : 'close'}`}
            </span>
          </button>
          <p className="text-xs text-slate-500 mt-2">
            When CLOSED, customers see &quot;Closed&quot; on your menu and cannot place orders — even
            during your operating hours below. Use this when you sell out, take a break, or end the
            day early.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            New-order text alerts
          </label>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={ownerNotificationPhone}
            onChange={(e) => setOwnerNotificationPhone(formatOwnerPhoneInput(e.target.value))}
            placeholder="+1 (555) 123-4567"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 min-h-[48px]"
          />
          <p className="text-xs text-slate-500 mt-1">
            We&apos;ll text this number every time a new paid order comes in, just in case the app
            isn&apos;t open. Use a US/Canada number. Standard message rates may apply. Leave blank
            to turn texts off.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            Operating hours
          </label>
          <p className="text-xs text-slate-500 mb-3">
            Leave empty for closed. Use 24h or HH:MM format.
          </p>
          <div className="space-y-2">
            {operatingHours.map((h) => (
              <div key={h.dayOfWeek} className="flex items-center gap-3">
                <span className="w-24 text-sm text-slate-700 dark:text-slate-300">
                  {DAY_NAMES[h.dayOfWeek]}
                </span>
                <input
                  type="time"
                  value={h.openTime || ''}
                  onChange={(e) => setHour(h.dayOfWeek, 'openTime', e.target.value)}
                  className="flex-1 max-w-[120px] px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                />
                <span className="text-slate-500">–</span>
                <input
                  type="time"
                  value={h.closeTime || ''}
                  onChange={(e) => setHour(h.dayOfWeek, 'closeTime', e.target.value)}
                  className="flex-1 max-w-[120px] px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                />
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="w-full py-4 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[52px]"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </div>
  );
}

